import { useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // SEO設定
  useSEO({
    title: '京都山科 お問い合わせ | 大石神社',
    description: '大石神社へのお問い合わせページです。御祈祷、授与品、行事、アクセスなどに関するご質問やご相談を承っております。お電話（075-581-5645）またはお問い合わせフォームよりお気軽にご連絡ください。受付時間：9:00〜17:00',
    keywords: 'お問い合わせ,連絡先,電話番号,メール,問い合わせフォーム,質問,相談,大石神社,京都,山科',
    canonical: '/contact',
    schema: generateWebPageSchema('お問い合わせ', '大石神社へのお問い合わせ。御祈祷、授与品、行事などに関するご質問を承ります', '/contact')
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d515vblpfatkmn6ifb0g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      
      <div className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-[#D4AF37] font-serif text-6xl font-bold mb-6 tracking-wide">お問い合わせ</h1>
            <div className="w-24 h-[1px] bg-[#D4AF37] mx-auto mb-8"></div>
            <p className="text-white text-lg leading-relaxed">
              ご質問やご相談がございましたら、お気軽にお問い合わせください。
            </p>
          </motion.div>

          <div className="grid grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="bg-[#1A1A1A] border border-[#D4AF37]/30 rounded-lg p-10">
                <h2 className="text-[#D4AF37] font-serif text-2xl font-bold mb-8 tracking-wide">
                  お問い合わせフォーム
                </h2>

                <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
                  <div className="space-y-6">
                    <div>
                      <label className="block text-[#D4AF37] text-sm mb-2 tracking-wide">
                        お名前 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full bg-black text-white border border-[#D4AF37]/50 rounded-md px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-all"
                        placeholder="山田太郎"
                      />
                    </div>

                    <div>
                      <label className="block text-[#D4AF37] text-sm mb-2 tracking-wide">
                        メールアドレス <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full bg-black text-white border border-[#D4AF37]/50 rounded-md px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-all"
                        placeholder="example@email.com"
                      />
                    </div>

                    <div>
                      <label className="block text-[#D4AF37] text-sm mb-2 tracking-wide">
                        電話番号
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full bg-black text-white border border-[#D4AF37]/50 rounded-md px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-all"
                        placeholder="090-1234-5678"
                      />
                    </div>

                    <div>
                      <label className="block text-[#D4AF37] text-sm mb-2 tracking-wide">
                        件名 <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full bg-black text-white border border-[#D4AF37]/50 rounded-md px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-all cursor-pointer"
                      >
                        <option value="">選択してください</option>
                        <option value="祈祷について">祈祷について</option>
                        <option value="授与品について">授与品について</option>
                        <option value="行事について">行事について</option>
                        <option value="アクセスについて">アクセスについて</option>
                        <option value="その他">その他</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-[#D4AF37] text-sm mb-2 tracking-wide">
                        お問い合わせ内容 <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        maxLength={500}
                        rows={6}
                        className="w-full bg-black text-white border border-[#D4AF37]/50 rounded-md px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-all resize-none"
                        placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                      />
                      <p className="text-gray-500 text-xs mt-1">{formData.message.length}/500文字</p>
                    </div>

                    {submitStatus === 'success' && (
                      <div className="bg-green-900/30 border border-green-500 text-green-300 px-4 py-3 rounded-md text-sm">
                        お問い合わせを受け付けました。ご返信までしばらくお待ちください。
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="bg-red-900/30 border border-red-500 text-red-300 px-4 py-3 rounded-md text-sm">
                        送信に失敗しました。お手数ですが、もう一度お試しください。
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#D4AF37] text-black font-semibold py-4 rounded-md hover:bg-[#C4A037] transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm tracking-wide cursor-pointer whitespace-nowrap"
                    >
                      {isSubmitting ? '送信中...' : '送信する'}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-8"
            >
              <div className="bg-[#1A1A1A] border border-[#D4AF37]/30 rounded-lg p-10">
                <h2 className="text-[#D4AF37] font-serif text-2xl font-bold mb-8 tracking-wide">
                  神社情報
                </h2>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-[#D4AF37] text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-2 tracking-wide">住所</h3>
                      <p className="text-gray-400 text-sm leading-relaxed">
                        〒607-8308<br />
                        京都府京都市山科区西野山桜ノ馬場町116
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-[#D4AF37] text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-2 tracking-wide">電話番号</h3>
                      <p className="text-gray-400 text-sm">075-581-5645</p>
                      <p className="text-gray-500 text-xs mt-1">受付時間：9:00〜17:00</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-[#D4AF37] text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-2 tracking-wide">メールアドレス</h3>
                      <p className="text-gray-400 text-sm">ooishijinja@gmail.com</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-[#D4AF37] text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-2 tracking-wide">社務所受付時間</h3>
                      <p className="text-gray-400 text-sm">9:00〜17:00</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-lg overflow-hidden border border-[#D4AF37]/30">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3269.8!2d135.8167!3d34.9833!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6001099e8e8e8e8e%3A0x8e8e8e8e8e8e8e8e!2z5aSn55-z56We56S-!5e0!3m2!1sja!2sjp!4v1234567890&z=16"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="大石神社の地図"
                ></iframe>
              </div>

              <div className="bg-[#D4AF37]/10 border border-[#D4AF37] rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line text-[#D4AF37] text-xl flex-shrink-0 mt-1"></i>
                  <div>
                    <h3 className="text-white font-semibold mb-2 text-sm tracking-wide">お問い合わせについて</h3>
                    <p className="text-gray-300 text-xs leading-relaxed">
                      お問い合わせへのご返信は、通常2〜3営業日以内に行っております。お急ぎの場合は、お電話にてお問い合わせください。
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ContactPage;
